/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
import PropTypes from 'prop-types';
import React from 'react';

import FieldSiret from 'Component/FieldSiret';
import Link from 'Component/Link';
import MyAccountConfirmIdForm from 'Component/MyAccountConfirmIdForm';
import { CUSTOMER_TYPES } from 'Component/MyAccountCreateAccount/MyAccountCreateAccount.config';
import {
    ACCOUNT_CUSTOMER_SERVICE_URL, GROUP_ID_EMPLOYEE,
    GROUP_ID_PRO, STATUS_ACCEPTE_SALARIE, STATUS_ATTENTE,
    STATUS_ATTENTE_NO_DOC, STATUS_ATTENTE_SALARIE, STATUS_ATTENTE_SALARIE_NO_DOC
} from 'Route/MyAccount/MyAccount.config';
import {
    MyAccountCustomerForm as SourceMyAccountCustomerForm
} from 'SourceComponent/MyAccountCustomerForm/MyAccountCustomerForm.component';
import transformToNameValuePair from 'Util/Form/Transform';
import { appendWithStoreCode } from 'Util/Url';

import { customerCompanyFields, customerInformationFields } from './MyAccountCustomerForm.form';

/** @namespace PeggysagePwa/Component/MyAccountCustomerForm/Component */
export class MyAccountCustomerFormComponent extends SourceMyAccountCustomerForm {
    static propTypes = {
        ...SourceMyAccountCustomerForm.propTypes,
        isSiretEnabled: PropTypes.bool.isRequired,
        isAttestationAvsEnabled: PropTypes.bool.isRequired,
        defaultCountry: PropTypes.string.isRequired,
        onChangeFile: PropTypes.func.isRequired,
        uploadStepList: PropTypes.arrayOf(PropTypes.shape({
            code: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            strings: PropTypes.objectOf(PropTypes.string)
        }))
    };

    /**
     * Override: remove siret from values if already set to prevent editing (security)
     * @param form
     * @param fields
     */
    onFormSuccess(form, fields) {
        const { onSave, customer: { siret } } = this.props;

        const filteredFields = siret ? fields.filter((field) => field.name !== 'siret') : fields;

        onSave(transformToNameValuePair(filteredFields));
    }

    /**
     * Override: duplicate of source component to take into account the override of form fields
     * @returns {*}
     */
    get customerInformationFieldMap() {
        const {
            showTaxVatNumber,
            handleChangeEmailCheckbox,
            handleChangePasswordCheckbox,
            showEmailChangeField,
            showPasswordChangeField,
            vatNumberRequired,
            customer: {
                firstname = '',
                lastname = '',
                taxvat = '',
                email = ''
            }
        } = this.props;

        return customerInformationFields({
            showTaxVatNumber,
            firstname,
            lastname,
            taxvat,
            email,
            handleChangePasswordCheckbox,
            handleChangeEmailCheckbox,
            showEmailChangeField,
            showPasswordChangeField,
            vatNumberRequired
        });
    }

    get companyFieldMap() {
        const {
            customer: {
                societe
            }
        } = this.props;

        return customerCompanyFields({
            societe
        });
    }

    renderCompanySiretField() {
        const {
            isSiretEnabled,
            customer: {
                siret
            }
        } = this.props;

        if (!isSiretEnabled) {
            return null;
        }

        return (
            <FieldSiret
              defaultValue={ siret }
              isDisabled={ !!siret }
            />
        );
    }

    renderCompanyNoSiret() {
        const { isSiretEnabled, customer: { siret, siret_comment } } = this.props;

        if (!isSiretEnabled || siret || !siret_comment) {
            return null;
        }

        return (
            <div block="FieldForm" elem="NoSiret">
                <strong>{ __('SIRET manquant') }</strong>
                <p>{ __('Raison : %s', siret_comment) }</p>
            </div>
        );
    }

    renderCompanyModificationMessage() {
        const url = appendWithStoreCode(ACCOUNT_CUSTOMER_SERVICE_URL);

        return (
            <div block="FieldForm" elem="ModifyCompanyMessage" className="AccountInfo">
                <p>
                    { __('Pour modifier les informations de votre entreprise, veuillez ') }
                    <Link to={ url }>
                        { __('contacter le service client') }
                    </Link>
                </p>
            </div>
        );
    }

    renderCompanyFields() {
        const { defaultCountry } = this.props;

        return (
            <>
                <legend block="FieldForm" elem="Legend">
                    { __('My company') }
                </legend>
                { this.companyFieldMap.map(this.renderSection) }
                { defaultCountry === 'FR' && (
                    <>
                        { this.renderCompanySiretField() }
                        { this.renderCompanyNoSiret() }
                    </>
                ) }
                { this.renderCompanyModificationMessage() }
            </>
        );
    }

    renderCompanySection() {
        const { customer: { is_pro, status_pro: accountStatus } } = this.props;

        if (!is_pro && ![STATUS_ATTENTE, STATUS_ATTENTE_NO_DOC].includes(accountStatus)) {
            return null;
        }
        if ([STATUS_ATTENTE_SALARIE, STATUS_ATTENTE_SALARIE_NO_DOC, STATUS_ACCEPTE_SALARIE].includes(accountStatus)) {
            return null;
        }

        return (
            <div
              block="FieldForm"
              elem="Section"
              mix={ { block: 'FieldForm', elem: 'SectionWithSpace', className: 'company' } }
            >
                { this.renderCompanyFields() }
            </div>
        );
    }

    renderValidateIdentitySection() {
        const {
            uploadStepList,
            customer: {
                group_id: customerGroupId = 0
            },
            onChangeFile
        } = this.props;

        if (!uploadStepList || !uploadStepList.length) {
            return null;
        }

        // eslint-disable-next-line no-nested-ternary
        const customerType = customerGroupId === GROUP_ID_PRO ? CUSTOMER_TYPES.pro : customerGroupId === GROUP_ID_EMPLOYEE ? CUSTOMER_TYPES.employee : CUSTOMER_TYPES.student;

        return uploadStepList.map((file) => (
            <div
              block="FieldForm"
              elem="Section"
              mix={ { block: 'FieldForm', elem: 'SectionWithSpace', className: 'identity' } }
              key={ file.code }
            >
                <legend block="FieldForm" elem="Legend">
                    { file.title }
                </legend>
                <MyAccountConfirmIdForm
                  customerType={ customerType }
                  fileType={ file.code }
                  formStrings={ file.strings }
                  onChangeFile={ onChangeFile }
                  isSkipDisabled
                  isSubmitDisabled
                />
            </div>
        ));
    }

    /**
     * Override: change submit label
     * @returns {JSX.Element}
     */
    renderActions() {
        return (
            <div block="FieldForm" elem="Actions">
                <button
                  type="submit"
                  block="Button"
                  mix={ { block: 'MyAccountInformation', elem: 'Submit' } }
                >
                    { __('Update') }
                </button>
            </div>
        );
    }

    /**
     * Override: replace legends by full translatable strings
     * @returns {JSX.Element|null}
     */
    renderEmailAndPasswordFields() {
        const { showEmailChangeField, showPasswordChangeField } = this.props;

        if (!showEmailChangeField && !showPasswordChangeField) {
            return null;
        }

        return (
            <>
                <legend
                  block="FieldForm"
                  elem="Legend"
                >
                    { this.getLegendString() }
                </legend>
                { this.emailAndPasswordFieldMap.map(this.renderSection) }
            </>
        );
    }

    /**
     * Override: return full translatable strings
     * @returns {string}
     */
    getLegendString() {
        const { showEmailChangeField, showPasswordChangeField } = this.props;

        if (showEmailChangeField && !showPasswordChangeField) {
            return __('Change Email');
        }

        if (!showEmailChangeField && showPasswordChangeField) {
            return __('Change Password');
        }

        return __('Change Email and Password');
    }

    /**
     * Override
     * @returns {JSX.Element}
     */
    renderFormBody() {
        return (
            <div block="FieldForm" elem="Body">
                <div block="FieldForm" elem="Fields">
                    <div block="FieldForm" elem="Section" className="account">
                        <legend block="FieldForm" elem="Legend">
                            { __('Account Information') }
                        </legend>
                        { this.customerInformationFieldMap.map(this.renderSection) }
                    </div>
                    <div
                      block="FieldForm"
                      elem="Section"
                      mix={ { block: 'FieldForm', elem: 'SectionWithSpace', className: 'emailPassword' } }
                    >
                        { this.renderEmailAndPasswordFields() }
                    </div>
                    { this.renderCompanySection() }
                    { /* this.renderValidateIdentitySection() */ }
                </div>
                { this.renderActions() }
            </div>
        );
    }
}

export default MyAccountCustomerFormComponent;
